import {delay} from "redux-saga";
import playSound from "../../../shared/sounds/playSound";
import {ActionType, getType} from "typesafe-actions";
import {answerQuestion, expireAnswerTimeout, resetAnswerTimer, showQuestion, skipQuestion} from "../actions";
import {call, cancel, fork, put, race, select, take, takeEvery} from "redux-saga/effects";
import {IApplicationState} from "../../index";
import {replaceQuestion} from "../../game/actions";
import {closeDialog, showDialog} from "../../dialog-manager/actions";
import {closeAds, watchAds} from "../../advertisement/actions";
import {showAlert} from "../../dialog-manager/dialogs/showAlert";
import {addCoins} from "../../player/actions";
import i18n from "../../../shared/i18n";

function* answerWillBeExpiredSoon(secondsForAnswer: number) {
    if (secondsForAnswer < 5)
        return;

    const tickCount = secondsForAnswer > 10
        ? 5
        : 3;

    yield delay((secondsForAnswer - tickCount) * 1000 - 400);

    for (let i = 0; i < tickCount; i++) {
        yield delay(1000);
        playSound("time-warning", 1);
    }
}

function* timeoutHandler() {
    yield put(showDialog({
        type: "PurchaseTimeDialog",
        options: {}
    }));

    const {payload} = yield take(getType(closeDialog));

    if (!payload)
        return;

    switch (payload.value) {
        case 'watchAd':
            const rewardedAdIsAvailable = yield select(({advertisement}: IApplicationState) => advertisement.rewardedAdIsAvailable);
            if (!rewardedAdIsAvailable)
                return;

            yield put(watchAds({
                adType: "rewarded",
                reward: "question_time"
            }));

            const {payload: {success}} = yield take(getType(closeAds));

            if (!success) {
                yield call(showAlert,
                    i18n("gameplay.rewardedad.failed.header"),
                    i18n("gameplay.rewardedad.failed.desc"));

                return;
            }

            playSound("question-select");
            return "ad";

        case 'buyWithCoins':
            const {coins, buyMoreTimeCost} = yield select(({player, settings}: IApplicationState) => ({
                coins: player.coins,
                buyMoreTimeCost: settings.gameBalance.buyMoreTimeCost,
            }));

            if (coins < buyMoreTimeCost)
                return;

            playSound("item-purchase");
            yield put(addCoins(-buyMoreTimeCost, "purchased_question_time"));

            return "coins";
    }

    return;
}

export function* watchEnterQuestion() {
    yield takeEvery([getType(showQuestion), getType(resetAnswerTimer)], function* (action: ActionType<typeof showQuestion> | ActionType<typeof resetAnswerTimer>) {
        const questionId = action.type === getType(resetAnswerTimer)
            ? action.payload.questionId
            : action.payload;

        const {secondsForAnswer, currentLevelId, progress} = yield select(({settings, game, questions}: IApplicationState) => ({
            secondsForAnswer: settings.gameBalance.secondsForAnswer,
            currentLevelId: game.levelInProgress,
            progress: questions.progress[questions.currentQuestionId]
        }));

        if (progress?.attempts < 0 || progress?.isTimeoutExpired || false)
            return;

        const answerExpireTask = yield fork(answerWillBeExpiredSoon, secondsForAnswer);

        const {isSkipped, isTimeout} = yield race({
            answer: take(getType(answerQuestion)),
            isTimeout: delay(secondsForAnswer * 1000),
            isSkipped: take(getType(replaceQuestion)),
        });

        yield cancel(answerExpireTask);

        if (isSkipped && (isSkipped as ActionType<typeof replaceQuestion>).payload.current === questionId) {
            yield put(skipQuestion({
                levelId: currentLevelId,
                questionId: questionId,
            }));
        }

        if (isTimeout) {
            const refreshTimerSource = yield call(timeoutHandler);

            if (refreshTimerSource) {
                yield put(resetAnswerTimer({
                    levelId: currentLevelId,
                    questionId: questionId,
                    resetTime: new Date().getTime()
                }, refreshTimerSource));
            } else {
                yield put(expireAnswerTimeout({
                    levelId: currentLevelId,
                    questionId: questionId,
                }));
            }
        }
    });
}